import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Package, Plus, X, Save } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';

const ProductBundleForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { apiCall } = useAuth();
  
  const [products, setProducts] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [bundleData, setBundleData] = useState({
    title: '',
    description: '',
    category: 'Product Bundles',
    sub_category: 'Bundle',
    bundlePrice: '',
    originalPrice: 0,
    discount: 0
  });

  useEffect(() => {
    fetchProducts();
  }, []);

  useEffect(() => {
    calculatePricing();
  }, [selectedProducts]);

  const fetchProducts = async () => {
    try {
      const data = await apiCall('/api/listings/?listing_type=Products&status=active');
      setProducts(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load products',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const calculatePricing = () => {
    const total = selectedProducts.reduce((sum, product) => {
      return sum + (parseFloat(product.data?.price) || 0);
    }, 0);
    
    const bundlePrice = parseFloat(bundleData.bundlePrice) || 0;
    const discount = total > 0 ? ((total - bundlePrice) / total * 100).toFixed(2) : 0;
    
    setBundleData(prev => ({
      ...prev,
      originalPrice: total,
      discount: discount
    }));
  };

  const toggleProductSelection = (product) => {
    setSelectedProducts(prev => {
      const exists = prev.find(p => p.id === product.id);
      if (exists) {
        return prev.filter(p => p.id !== product.id);
      } else {
        return [...prev, product];
      }
    });
  };

  const handleSubmit = async () => {
    if (!bundleData.title.trim()) {
      toast({
        title: 'Error',
        description: 'Please enter bundle name',
        variant: 'destructive'
      });
      return;
    }

    if (selectedProducts.length < 2) {
      toast({
        title: 'Error',
        description: 'Please select at least 2 products for the bundle',
        variant: 'destructive'
      });
      return;
    }

    if (!bundleData.bundlePrice || parseFloat(bundleData.bundlePrice) <= 0) {
      toast({
        title: 'Error',
        description: 'Please enter a valid bundle price',
        variant: 'destructive'
      });
      return;
    }

    try {
      setSaving(true);
      
      const bundlePayload = {
        title: bundleData.title,
        description: bundleData.description,
        listing_type: 'product',
        category: bundleData.category,
        sub_category: bundleData.sub_category,
        status: 'draft',
        media: [],
        data: {
          price: parseFloat(bundleData.bundlePrice),
          original_price: bundleData.originalPrice,
          discount_percentage: bundleData.discount,
          bundle_items: selectedProducts.map(p => ({
            product_id: p.id,
            title: p.title,
            price: p.data?.price || 0
          })),
          is_bundle: true,
          inventory: Math.min(...selectedProducts.map(p => p.data?.inventory || 0))
        }
      };

      await apiCall('/api/listings/', {
        method: 'POST',
        body: JSON.stringify(bundlePayload)
      });

      toast({
        title: 'Success',
        description: 'Product bundle created successfully'
      });

      navigate('/listings/products');
    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <p className="text-gray-500">Loading products...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 max-w-6xl">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/listings/products')}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Products
          </Button>
        </div>

        <div className="flex items-center gap-3">
          <Package className="h-8 w-8 text-blue-600" />
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Create Product Bundle</h1>
            <p className="text-gray-600">Combine multiple products into a discounted bundle</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left Column - Bundle Details */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Bundle Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="bundle-name">Bundle Name *</Label>
                  <Input
                    id="bundle-name"
                    placeholder="e.g., Complete Camera Kit"
                    value={bundleData.title}
                    onChange={(e) => setBundleData({ ...bundleData, title: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bundle-description">Description</Label>
                  <Textarea
                    id="bundle-description"
                    placeholder="Describe what's included in this bundle..."
                    rows={4}
                    value={bundleData.description}
                    onChange={(e) => setBundleData({ ...bundleData, description: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="bundle-price">Bundle Price (₹) *</Label>
                  <Input
                    id="bundle-price"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="Enter bundle price"
                    value={bundleData.bundlePrice}
                    onChange={(e) => setBundleData({ ...bundleData, bundlePrice: e.target.value })}
                  />
                </div>

                <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Original Total:</span>
                    <span className="font-semibold">₹{bundleData.originalPrice.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Bundle Price:</span>
                    <span className="font-semibold">₹{parseFloat(bundleData.bundlePrice || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-green-600 font-semibold">You Save:</span>
                    <span className="text-green-600 font-semibold">
                      ₹{(bundleData.originalPrice - parseFloat(bundleData.bundlePrice || 0)).toFixed(2)}
                      {bundleData.discount > 0 && ` (${bundleData.discount}%)`}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Selected Products ({selectedProducts.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {selectedProducts.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No products selected yet</p>
                ) : (
                  <div className="space-y-2">
                    {selectedProducts.map(product => (
                      <div key={product.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex-1">
                          <p className="font-medium">{product.title}</p>
                          <p className="text-sm text-gray-600">₹{product.data?.price || 0}</p>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleProductSelection(product)}
                        >
                          <X className="h-4 w-4 text-red-600" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Product Selection */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>Select Products to Include</CardTitle>
                <p className="text-sm text-gray-600">Choose at least 2 products</p>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {products.length === 0 ? (
                    <p className="text-gray-500 text-center py-4">No active products available</p>
                  ) : (
                    products.map(product => {
                      const isSelected = selectedProducts.find(p => p.id === product.id);
                      return (
                        <div
                          key={product.id}
                          className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:border-blue-500 transition-colors ${
                            isSelected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                          }`}
                          onClick={() => toggleProductSelection(product)}
                        >
                          <Checkbox
                            checked={!!isSelected}
                            onCheckedChange={() => toggleProductSelection(product)}
                            className="mt-1"
                          />
                          <div className="flex-1">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <h4 className="font-medium">{product.title}</h4>
                                <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                                  {product.description}
                                </p>
                                <div className="flex items-center gap-2 mt-2">
                                  <Badge variant="outline">{product.category}</Badge>
                                  <span className="text-sm text-gray-600">
                                    Stock: {product.data?.inventory || 0}
                                  </span>
                                </div>
                              </div>
                              <span className="font-semibold text-lg ml-3">
                                ₹{product.data?.price || 0}
                              </span>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate('/listings/products')}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving}>
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Creating Bundle...' : 'Create Bundle'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ProductBundleForm;
